/**
 * Where the three things a step screen is made of actually SIT (spec §2.1).
 *
 * Desktop: rail · step · phone, the step column first in the DOM so Tab
 * reaches the control before the navigation and `order` puts the rail on the
 * left. The grid is capped at 1280 px and centred: on a 2000 px window the
 * step column used to hug the left edge while the phone floated mid-right
 * (operator, round 2) — now the whole wizard sits in the middle and the
 * columns keep their proportions. Adjust collapses it to two FULL-width
 * columns — the Focus editor brings its own preview and needs the room, and a
 * second differently-sized copy of the same frame beside it would only invite
 * the question "which one is real?".
 *
 * Phone: a 44 px bar, the sample, and the step in a scrolling sheet beneath
 * it — the phone never scrolls, the step does.
 */
import type { ReactNode } from "react";
import { MoreHorizontal, X } from "lucide-react";
import { MONO_LABEL } from "./ui";

export interface WizardLayoutProps {
  wide: boolean;
  /** ≥ 1100 px: the step column gets its own fixed width. */
  roomy: boolean;
  /** True while Review › Adjust owns the screen. */
  adjusting: boolean;
  /** How much of the viewport sits ABOVE the wizard (the app shell's header,
   *  where there is one). */
  topInset: number;
  chapterLabel: string;
  counter: { n: number; total: number } | null;
  step: ReactNode;
  rail: ReactNode;
  phone: ReactNode;
  onExit: () => void;
  onStartOver: () => void;
}

export function WizardLayout(p: WizardLayoutProps): React.JSX.Element {
  const { wide, roomy, adjusting, topInset, chapterLabel, counter, step, rail, phone } = p;
  return wide ? (
        <div
          style={{
            display: "grid",
            // Adjust IS the second column: the Focus editor brings its own
            // preview, so the sample phone stands down rather than showing a
            // second, differently-sized copy of the same frame.
            gridTemplateColumns: adjusting
              ? "200px minmax(0, 1fr)"
              : roomy
                ? "200px minmax(380px, 520px) minmax(360px, 1fr)"
                : "180px minmax(0, 1fr) minmax(0, 1fr)",
            // Centred, not left-hugging: the three columns stop growing at
            // 1280 px and the block sits in the middle of anything wider.
            // Adjust gets more room (the Focus editor's panel + stage) but
            // stops at 1640 px too — on a 2000 px window the panel sat on
            // the far left and the phone on the far right (operator, 2026-09-16).
            ...(adjusting
              ? { maxWidth: 1640, margin: "0 auto" }
              : { maxWidth: 1280, margin: "0 auto", justifyContent: "center" }),
            gap: 28,
            alignItems: "center",
            minHeight: `calc(100dvh - ${topInset}px)`,
            padding: "24px 20px",
            boxSizing: "border-box",
          }}
        >
          {/* The step column is FIRST in the DOM so Tab reaches the control
              before the navigation; `order` puts the rail on the left. */}
          <div
            style={{
              order: 1,
              minWidth: 0,
              maxHeight: `calc(100dvh - ${topInset}px)`,
              overflowY: "auto",
              padding: "8px 2px",
            }}
          >
            {step}
          </div>
          <div style={{ order: 0, minWidth: 0, alignSelf: "center" }}>{rail}</div>
          {!adjusting && (
            <div style={{ order: 2, minWidth: 0, display: "flex", justifyContent: "center" }}>{phone}</div>
          )}
        </div>
  ) : (
    /* The phone NEVER scrolls on a phone (spec §2.1): the column IS the
       viewport, and only the step underneath it moves. */
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: `calc(100dvh - ${topInset}px)`,
            overflow: "hidden",
            paddingTop: 3,
          }}
        >
          <div
            style={{
              height: 44,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "0 14px",
              borderBottom: "1px solid rgba(255,255,255,.07)",
            }}
          >
            <button
              type="button"
              onClick={p.onExit}
              aria-label="Exit setup"
              style={{ border: "none", background: "transparent", color: "var(--ql-fg-subtle)", cursor: "pointer", display: "inline-flex", padding: 4 }}
            >
              <X size={15} />
            </button>
            <span style={{ ...MONO_LABEL, margin: "0 auto" }}>
              {chapterLabel}
              {counter ? ` · ${counter.n} of ${counter.total}` : ""}
            </span>
            <button
              type="button"
              onClick={p.onStartOver}
              aria-label="Start over"
              style={{ border: "none", background: "transparent", color: "var(--ql-fg-subtle)", cursor: "pointer", display: "inline-flex", padding: 4 }}
            >
              <MoreHorizontal size={16} />
            </button>
          </div>
          {!adjusting && (
            <div style={{ display: "flex", justifyContent: "center", flexShrink: 0, padding: "12px 0 10px" }}>
              {phone}
            </div>
          )}
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 16px 0", minWidth: 0 }}>{step}</div>
        </div>
  );
}
